import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import GironeBadge from "@/components/GironeBadge";
import Footer from "@/components/Footer";

/**
 * Landing pubblica del link di invito supporter.
 * URL: /supporter/join/:token
 * Non richiede autenticazione. L'utente inserisce nome+cognome+email e conferma
 * o rifiuta la richiesta di diventare supporter del fantallenatore.
 */
export default function SupporterJoin() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [err, setErr] = useState(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "accepted" | "declined"

  useEffect(() => {
    api.get(`/public/supporter/request/${token}`)
      .then(({ data }) => setReq(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link non valido o scaduto"));
  }, [token]);

  const accept = async () => {
    if (fullName.trim().length < 3) return toast.error("Inserisci nome e cognome (min 3 caratteri)");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Email non valida");
    setBusy(true);
    try {
      await api.post(`/public/supporter/request/${token}/accept`, {
        full_name: fullName.trim(),
        email: email.trim(),
      });
      setDone("accepted");
      toast.success("Sei ora supporter! Grazie 💪");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore. Riprova.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm("Confermi di voler rifiutare la richiesta?")) return;
    setBusy(true);
    try {
      await api.post(`/public/supporter/request/${token}/decline`);
      setDone("declined");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore. Riprova.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(circle at 20% 0%, #0b1220 0%, #050914 45%, #000 100%)" }}>
      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-slate-900 text-white" data-testid="supporter-join-card">
          <div className="relative p-6 md:p-8"
               style={{ background: "linear-gradient(135deg, #f472b6 0%, #db2777 55%, #831843 100%)" }}>
            <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full blur-3xl opacity-40" style={{ background: "#fff" }} />
            <div className="relative flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-white/95 flex items-center justify-center shrink-0">
                <img src="/logo-fife-192.png" alt="FIFE" className="w-11 h-11 object-contain aspect-square" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/85">Fantacalcio FIFE 2026/27</div>
                <div className="display font-black text-2xl md:text-3xl leading-tight">Diventa Supporter</div>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {err && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-100 p-4 flex items-start gap-2" data-testid="supporter-join-error">
                <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-black">Link non valido</div>
                  <div className="text-sm text-white/80 mt-0.5">{String(err)}</div>
                </div>
              </div>
            )}

            {!req && !err && (
              <div className="flex items-center justify-center py-8 text-white/60" data-testid="supporter-join-loading">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifico il link…
              </div>
            )}

            {req && done === "accepted" && (
              <div className="text-center py-6" data-testid="supporter-join-accepted">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="display font-black text-2xl">Benvenuto tra i tifosi di {req.target_team}!</div>
                <p className="text-white/70 mt-2 text-sm">
                  Ora {req.target_name || "il fantallenatore"} ha un supporter in più. Grazie per aver risposto alla chiamata.
                </p>
                <Link to="/" className="inline-block mt-6 rounded-full px-5 h-10 leading-10 text-sm font-black uppercase tracking-widest bg-white text-slate-900">
                  Scopri FIFE
                </Link>
              </div>
            )}

            {req && done === "declined" && (
              <div className="text-center py-6" data-testid="supporter-join-declined">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8" }}>
                  <XCircle className="w-8 h-8" />
                </div>
                <div className="display font-black text-xl">Richiesta rifiutata</div>
                <p className="text-white/70 mt-2 text-sm">
                  Nessun problema — se cambi idea potrai comunque ricevere un nuovo link in futuro.
                </p>
              </div>
            )}

            {req && !done && req.status !== "pending" && (
              <div className="text-center py-6" data-testid="supporter-join-expired">
                <div className="display font-black text-xl">Questo link è già stato usato</div>
                <p className="text-white/70 mt-2 text-sm">Chiedi un nuovo invito al fantallenatore.</p>
              </div>
            )}

            {req && !done && req.status === "pending" && (
              <div className="space-y-5" data-testid="supporter-join-form">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-white/95 flex items-center justify-center shrink-0">
                    <img src="/logo-fife-192.png" alt="team" className="w-10 h-10 object-contain aspect-square" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60">
                      {req.girone && <GironeBadge girone={req.girone} size={14} />}
                      <span>Girone {req.girone || "—"}</span>
                    </div>
                    <div className="display font-black text-xl leading-tight truncate">{req.target_team}</div>
                    {req.target_name && <div className="text-white/75 text-sm truncate">Fantall.: {req.target_name}</div>}
                  </div>
                </div>

                <div className="text-sm text-white/80 leading-relaxed">
                  <b>{req.target_name || "Il fantallenatore"}</b> ti sta chiedendo di diventare suo <span style={{ color: "#f472b6" }} className="font-bold">supporter</span>. Ogni supporter porta <b>+1 Gufo</b> alla sua squadra e viene visualizzato nella sua area riservata.
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60">Nome e cognome</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value.slice(0, 80))}
                      placeholder="Mario Rossi"
                      className="mt-2 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                      data-testid="supporter-join-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60">La tua email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.slice(0, 120))}
                      placeholder="mario@example.com"
                      className="mt-2 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                      data-testid="supporter-join-email"
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={decline}
                    disabled={busy}
                    data-testid="supporter-join-decline"
                    className="rounded-full h-11 sm:flex-1 bg-transparent border-white/25 text-white/85 hover:bg-white/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Rifiuta
                  </Button>
                  <Button
                    onClick={accept}
                    disabled={busy || !fullName || !email}
                    data-testid="supporter-join-accept"
                    className="rounded-full h-11 sm:flex-1 font-black uppercase tracking-widest"
                    style={{ background: "#f472b6", color: "#0f172a" }}
                  >
                    <Heart className="w-4 h-4 mr-2" /> Diventa supporter
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
